import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, FocusEvent } from "react";
import type { InputProps } from "ra-core";
import { FieldTitle, useInput, useResourceContext } from "ra-core";
import {
  FormControl,
  FormError,
  FormField,
  FormLabel,
} from "@/components/admin/form";
import { InputHelperText } from "@/components/admin/input-helper-text";
import { Input } from "@/components/ui/input";

import { centsToDollars, dollarsToCents } from "./classUtils";

/**
 * Edits a price stored in cents as dollars. The form value stays in cents so
 * the database never sees floats. Like NumberInput, it keeps the typed text
 * locally so half-typed values ("15.") are not reformatted mid-keystroke.
 */
export const PriceInput = (props: InputProps & { className?: string }) => {
  const { label, source, className, helperText } = props;
  const resource = useResourceContext(props);
  const { id, field, isRequired } = useInput(props);
  const [text, setText] = useState(centsToDollars(field.value));
  const hasFocus = useRef(false);

  useEffect(() => {
    if (!hasFocus.current) setText(centsToDollars(field.value));
  }, [field.value]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setText(event.target.value);
    field.onChange(dollarsToCents(event.target.value));
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    hasFocus.current = false;
    field.onBlur?.(event);
    setText(centsToDollars(field.value));
  };

  return (
    <FormField id={id} className={className} name={field.name}>
      {label !== false && (
        <FormLabel>
          <FieldTitle
            label={label}
            source={source}
            resource={resource}
            isRequired={isRequired}
          />
        </FormLabel>
      )}
      <FormControl>
        <Input
          name={field.name}
          ref={field.ref}
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          value={text}
          onChange={handleChange}
          onFocus={() => {
            hasFocus.current = true;
          }}
          onBlur={handleBlur}
        />
      </FormControl>
      <InputHelperText helperText={helperText} />
      <FormError />
    </FormField>
  );
};
